import { LoadingButton } from '@mui/lab';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Divider, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { DialogCloseButton } from 'src/shared/components/organisms/dialogCloseButton';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { toast } from 'react-toastify';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useTranslation } from 'react-i18next';
import { getSwishConsentById, revokeSwishConsent } from 'src/shared/services/swishConsentService';
import { SwishConsent } from '../models/swishConsent';

type SwishConsentModalProps = {
  modalOpen: boolean;
  swishConsentId: number;
  onSave: () => void;
  onClose: () => void;
};

export const SwishConsentModal = (props: SwishConsentModalProps) => {
  const { modalOpen, swishConsentId, onSave, onClose } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [consent, setConsent] = useState<SwishConsent>();

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  // Översätt medgivandestatus (CREATED/ACTIVE/...) med fallback till råvärdet.
  const consentStatusLabel = (status?: string | null) => {
    if (!status) return '';
    const key = `invoice:swishStatus.consentStatusValues.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSwishConsentById(swishConsentId);
      if (isMounted()) {
        setConsent(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [swishConsentId, isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  // Endast aktiva/pågående medgivanden kan återkallas (inte redan återkallade/borttagna).
  const canRevoke = consent ? consent.status !== 'REVOKED' && consent.status !== 'DELETED' : false;

  const handleRevoke = useCallback(async () => {
    if (!consent) return;
    setLoading(true);
    try {
      await revokeSwishConsent(consent.swishConsent_id);
      if (isMounted()) {
        toast.success(t('invoice:swishStatus.revokeSuccess') as string);
        onSave();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(t('invoice:swishStatus.revokeError') as string);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [consent, isMounted, onSave, t]);

  const GridItem = ({ label, value }) => {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <Typography variant='caption' sx={{ lineHeight: 1, color: 'text.disabled' }}>
          {label}:
        </Typography>
        <Typography variant='body1' sx={{ fontWeight: 500, lineHeight: 1 }}>
          {value || '—'}
        </Typography>
      </Grid>
    );
  };

  return (
    <Dialog
      onClose={onHandleClose}
      aria-labelledby={`${componentId}-titel`}
      open={modalOpen}
      maxWidth='md'
      fullWidth
      sx={{
        minWidth: {
          xs: '90vw',
          md: '400px',
        },
        '& .MuiDialogContent-root': {
          p: 2,
        },
        '& .MuiDialogActions-root': {
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, lineHeight: 1.2 }} id={`${componentId}-titel`}>
        {t('invoice:swishStatus.consents.modalTitle')}
        <DialogCloseButton handleClose={onHandleClose} />
      </DialogTitle>
      <LoadingOverlay loading={loading} />
      {consent && (
        <DialogContent>
          <Grid container spacing={2}>
            <GridItem label={t('invoice:swishStatus.consents.labels.payerAlias')} value={consent.payerAlias} />
            <GridItem label={t('invoice:swishStatus.consents.labels.payeeAlias')} value={consent.payeeAlias} />
            <GridItem label={t('invoice:swishStatus.consents.labels.consentId')} value={consent.consentId} />
            <GridItem label={t('invoice:swishStatus.consents.labels.status')} value={consentStatusLabel(consent.status)} />
            <GridItem label={t('invoice:swishStatus.consents.labels.subStatus')} value={consent.subStatus} />
            <GridItem
              label={t('invoice:swishStatus.consents.labels.activatedAt')}
              value={consent.activatedAt ? DateHelper.getDateTime(consent.activatedAt) : ''}
            />
            <GridItem label={t('invoice:swishStatus.consents.labels.createdAt')} value={DateHelper.getDateTime(consent.createdAt)} />
            <GridItem
              label={t('invoice:swishStatus.consents.labels.revokedAt')}
              value={consent.revokedAt ? DateHelper.getDateTime(consent.revokedAt) : ''}
            />
          </Grid>
        </DialogContent>
      )}
      <Divider />
      <DialogActions>
        <Box sx={{ p: 1 }}>
          <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose} sx={{ mr: 2 }}>
            {t('common:button.cancel')}
          </Button>
          <LoadingButton disabled={loading || !canRevoke} autoFocus variant='contained' color='error' onClick={handleRevoke}>
            {t('invoice:swishStatus.revoke')}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
